import { type FormEvent, useRef, useState } from "react";
import { Activity, Eye, EyeOff, Headphones, Compass, UserRound, UserRoundPlus, LockKeyhole, LogIn } from "lucide-react";
import { api, ApiError } from "../api";
import { CaptchaField, useCaptcha } from "../components/CaptchaField";
import { Button, Field, Modal } from "../components/ui";
import type { User } from "../types";

export function LoginModal({ open, onClose, onSuccess, initialError, onSupport, onBrowse }: { initialError: string; open: boolean; onClose: () => void; onSuccess: (message: string, user: User) => void; onSupport: () => void; onBrowse: () => void }) {
  const [mode, setMode] = useState<"login" | "register">(new URLSearchParams(window.location.search).has("ref") ? "register" : "login");
  const [referralCode, setReferralCode] = useState(new URLSearchParams(window.location.search).get("ref")?.slice(0, 40) ?? "");
  const [username, setUsername] = useState(import.meta.env.DEV ? "xinghe888" : "");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState(import.meta.env.DEV ? "xh888888" : "");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState<{ username?: string; displayName?: string; password?: string; referralCode?: string }>({});
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const captcha = useCaptcha(mode, open, { username, role: "user" });
  const userRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const focusInvalid = () => requestAnimationFrame(() => formRef.current?.querySelector<HTMLInputElement>("[aria-invalid=true]")?.focus());

  function close() {
    if (lock.current) return;
    setPassword(""); setConfirmation(""); setShowPassword(false);
    onClose();
  }

  function switchMode(next: "login" | "register") {
    if (lock.current) return;
    setMode(next);
    setError("");
    setFieldError({});
    setPassword("");
    setConfirmation("");
    setShowPassword(false);
    window.setTimeout(() => userRef.current?.focus(), 0);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (lock.current) return;
    setError("");
    setFieldError({});
    if (!username.trim()) {
      setFieldError({ username: "请输入账号。" });
      userRef.current?.focus();
      return;
    }
    if (mode === "register" && !displayName.trim()) {
      setFieldError({ displayName: "请输入昵称。" });
      focusInvalid();
      return;
    }
    if (!password) {
      setFieldError({ password: "请输入密码。" });
      focusInvalid();
      return;
    }
    if (mode === "register" && password !== confirmation) {
      setFieldError({ password: "两次输入的密码不一致。" });
      focusInvalid();
      return;
    }
    const security = captcha.submission(); if (!security) return;
    lock.current = true; setBusy(true);
    try {
      if (mode === "login") {
        const result = await api<{ user: User }>("/api/auth/login", { method: "POST", signal: AbortSignal.timeout(15000), body: JSON.stringify({ username, password, role: "user", ...security }) });
        captcha.invalidate(); onSuccess("欢迎回到西海岸", result.user);
      } else {
        const result = await api<{ user: User }>("/api/auth/register", { method: "POST", signal: AbortSignal.timeout(15000), body: JSON.stringify({ username, displayName, password, referralCode, ...security }) });
        captcha.invalidate(); onSuccess("注册成功，欢迎加入西海岸", result.user);
      }
      setPassword("");
      setConfirmation("");
      setReferralCode("");
      const nextUrl = new URL(window.location.href); nextUrl.searchParams.delete("ref"); window.history.replaceState(window.history.state, "", `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    } catch (reason) {
      if (captcha.recover(reason)) return;
      if (reason instanceof ApiError && ["username", "displayName", "password", "referralCode"].includes(reason.field ?? "")) {
        setFieldError({ [reason.field!]: reason.message });
        focusInvalid();
      } else {
        setError(reason instanceof ApiError ? reason.message : `${mode === "login" ? "登录" : "注册"}失败，请稍后重试。`);
      }
    } finally {
      lock.current = false; setBusy(false);
    }
  }

  return (
    <Modal variant="auth" open={open} onClose={close} preventClose={busy} title={mode === "login" ? "登录西海岸账号" : "注册西海岸账号"}>
      <div className="coast-auth__brand" aria-hidden="true"><img src="/assets/west-coast/app-icon.png" alt="" width="196" height="196"/><small>WEST COAST</small></div>
      {mode === "register" && <p className="coast-auth__welcome">创建你的西海岸账号</p>}
      {initialError && <div className="form-alert" role="alert">{initialError}</div>}
      <form ref={formRef} className="form-stack coast-auth__form" noValidate onSubmit={submit}>
        {error && <div id="public-login-error" className="form-alert" role="alert"><Activity aria-hidden="true" />{error}</div>}
        <div className="coast-auth__account"><UserRound aria-hidden="true"/><Field ref={userRef} label="账号" aria-label="账号" placeholder="请输入账号" error={fieldError.username} hint={mode === "register" ? "4–24 位，以字母开头，可使用小写字母、数字和下划线。" : undefined} autoCapitalize="none" spellCheck={false} autoComplete="username" disabled={busy} value={username} onChange={(event) => { setUsername(event.target.value); setError(""); setFieldError((value) => ({ ...value, username: undefined })); }} /></div>
        {mode === "register" && <Field label="昵称" aria-label="昵称" error={fieldError.displayName} autoComplete="name" disabled={busy} value={displayName} onChange={(event) => { setDisplayName(event.target.value); setFieldError((value) => ({ ...value, displayName: undefined })); }} />}
        <div className="field coast-auth__password">
          <label className="field__label" htmlFor="public-password">密码</label>
          <span className="secret-field"><LockKeyhole className="coast-auth__field-icon" aria-hidden="true"/><input id="public-password" aria-invalid={Boolean(fieldError.password)} aria-describedby="public-password-message" className="input" placeholder="请输入密码" type={showPassword ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} value={password} disabled={busy} onChange={(event) => { setPassword(event.target.value); setError(""); setFieldError((value) => ({ ...value, password: undefined })); }} /><button type="button" aria-label={showPassword ? "隐藏密码" : "显示密码"} aria-pressed={showPassword} onClick={() => setShowPassword((value) => !value)}>{showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}</button></span>
          <span id="public-password-message" className={`field__message ${fieldError.password ? "field__message--error" : ""}`}>{fieldError.password ?? (mode === "register" ? "8–128 位，须同时包含字母和数字。" : "\u00A0")}</span>
        </div>
        {mode === "register" && <Field label="确认密码" type={showPassword ? "text" : "password"} autoComplete="new-password" disabled={busy} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />}
        {mode === "login" && import.meta.env.DEV && <button className="auth-test-account" type="button" onClick={() => { setUsername("xinghe_test"); setPassword("test123456"); setError(""); setFieldError({}); }}>填入测试账号 xinghe_test</button>}
        {mode === "register" && <Field label="推广码（选填）" hint="通过推广链接注册会自动填写，注册完成后归属固定。" value={referralCode} disabled={busy} error={fieldError.referralCode} onChange={event => { setReferralCode(event.target.value); setFieldError(value => ({ ...value, referralCode: undefined })); }} />}
        <CaptchaField captcha={captcha} disabled={busy} />
        <Button type="submit" busy={busy} disabled={!captcha.ready}>{mode === "login" ? "登录" : "注册并登录"}</Button>
      </form>
      <div className="coast-auth__help"><button type="button" disabled={busy} onClick={() => { close(); onSupport(); }}>忘记密码？联系客服</button><span>可使用浏览器安全保存的密码</span></div>
      <div className="coast-auth__actions"><Button emphasis="outline" disabled={busy} onClick={() => switchMode(mode === "login" ? "register" : "login")}>{mode === "login" ? <UserRoundPlus aria-hidden="true"/> : <LogIn aria-hidden="true"/>}{mode === "login" ? "创建账号" : "账号登录"}</Button><Button emphasis="outline" disabled={busy} onClick={() => { close(); onBrowse(); }}><Compass aria-hidden="true"/>先逛逛</Button><Button emphasis="outline" disabled={busy} onClick={() => { close(); onSupport(); }}><Headphones aria-hidden="true"/>联系客服</Button></div>
    </Modal>
  );
}
